import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const steps = [
    "Discovering your true Persona...",
    "Mapping your Brain Balance...",
    "Extracting your Top Strengths...",
    "Pinpointing your Growth Areas...",
    "Finalizing your Psychological Profile..."
];

export const AnalysisLoadingScreen = () => {
    const [step, setStep] = useState(0);

    // Cycle through steps
    useEffect(() => {
        const interval = setInterval(() => {
            setStep((prev) => (prev + 1) % steps.length);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="flex flex-col items-center justify-center w-full min-h-[320px] gap-8 relative bg-transparent py-4">
            {/* Central Neural Node */}
            <div className="relative flex items-center justify-center w-48 h-48 flex-shrink-0">

                {/* 1. Outer Rotating Ring (Dashed) */}
                <motion.div
                    className="absolute w-40 h-40 border-2 border-dashed border-blue-500/20 rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                />

                {/* 2. Inner Rotating Ring (Reverse) */}
                <motion.div
                    className="absolute w-32 h-32 border-2 border-dotted border-purple-500/30 rounded-full"
                    animate={{ rotate: -360 }}
                    transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                />

                {/* 3. Pulsing Core Glow */}
                <motion.div
                    className="absolute w-20 h-20 bg-blue-500/10 rounded-full blur-xl"
                    animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />

                {/* 4. Central Neural Hub (Geometric) */}
                <div className="relative z-10 w-16 h-16 flex items-center justify-center">
                    <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-[0_0_15px_rgba(139,92,246,0.6)]">
                        <motion.path
                            d="M50 5 L95 27 L95 73 L50 95 L5 73 L5 27 Z"
                            fill="none"
                            stroke="url(#grad1)"
                            strokeWidth="4"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                        />
                        <defs>
                            <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#3b82f6" />
                                <stop offset="100%" stopColor="#8b5cf6" />
                            </linearGradient>
                        </defs>
                    </svg>

                    {/* Inner Node */}
                    <motion.div
                        className="absolute w-2 h-2 bg-white rounded-full shadow-[0_0_10px_white]"
                        animate={{ opacity: [0.2, 1, 0.2] }}
                        transition={{ duration: 1, repeat: Infinity }}
                    />
                </div>

                {/* 5. Orbiting Data Particles — clamped within the 192px box */}
                <motion.div
                    className="absolute w-full h-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                >
                    <div className="absolute top-0 left-1/2 w-2 h-2 bg-blue-400 rounded-full -translate-x-1/2 -translate-y-[60px] shadow-[0_0_5px_#60a5fa]" />
                </motion.div>
                <motion.div
                    className="absolute w-full h-full"
                    animate={{ rotate: -360 }}
                    transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                >
                    <div className="absolute bottom-0 left-1/2 w-1.5 h-1.5 bg-purple-400 rounded-full -translate-x-1/2 translate-y-[60px]" />
                </motion.div>

            </div>

            {/* Dynamic Status Text */}
            <div className="flex flex-col items-center gap-2 text-center px-4">
                <motion.div
                    key={step}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="flex flex-col items-center"
                >
                    <p className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 tracking-wide">
                        {steps[step]}
                    </p>
                    <p className="text-xs text-gray-400 mt-1 uppercase tracking-widest opacity-70">
                        Generating Your Cards
                    </p>
                </motion.div>

                {/* Progress Line */}
                <div className="w-32 h-0.5 bg-gray-800 rounded-full mt-3 overflow-hidden">
                    <motion.div
                        className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                        initial={{ width: "0%" }}
                        animate={{ width: "100%" }}
                        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                    />
                </div>
            </div>
        </div>
    );
};
